import React, { useState } from "react";
import { Modal, Header, Message, Form, Input, Button, Icon } from "semantic-ui-react";
import { parseCSVLine } from "../lib/helperServices";

function downloadCsvTemplate(filename, headers, sampleRow) {
  const csvContent = headers.join(",") + "\n" + sampleRow;
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function parseCsvFile(file, minColumns, rowToItem) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const lines = event.target.result
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const row = parseCSVLine(lines[i]);
        if (row.length < minColumns) {
          console.error(
            `Skipping row - insufficient columns (need ${minColumns}, got ${row.length})`
          );
          continue;
        }
        if (row.every((cell) => cell === "")) continue;
        items.push(rowToItem(row));
      }
      resolve(items);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

// The bulk endpoints respond with either a bare results array or
// { results: [...], ... }, depending on whether the summary record saved.
function extractResultsList(responseData) {
  if (Array.isArray(responseData)) return responseData;
  if (responseData && Array.isArray(responseData.results)) return responseData.results;
  return [];
}

/**
 * Generic bulk-CSV-upload modal shared by exam and booking bulk import.
 * Owns the file-picker/parse/upload/results state; callers supply the CSV
 * shape, the upload call, and how to render the per-row results.
 */
export default function BulkUploadModal({
  open,
  onClose,
  title,
  downloadLabel,
  formatInstructions,
  templateFilename,
  templateHeaders,
  templateSampleRow,
  minColumns,
  rowToItem,
  uploadItems,
  renderResults,
  onUploaded,
}) {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      const itemsRaw = await parseCsvFile(file, minColumns, rowToItem);
      const response = await uploadItems(itemsRaw);
      setResults(extractResultsList(response.data));
      onUploaded?.(true);
    } catch (e) {
      setResults([{ error: "Bulk upload failed." }]);
      onUploaded?.(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Header icon="upload" content={title} />
      <Modal.Content>
        <Message info>
          <Message.Header>
            Please download the template first, fill it out, then upload it here.
          </Message.Header>
          {formatInstructions}
          <p>
            <Icon name="download" />
            <span
              style={{ cursor: "pointer", color: "#2185d0" }}
              onClick={() =>
                downloadCsvTemplate(templateFilename, templateHeaders, templateSampleRow)
              }
            >
              {downloadLabel}
            </span>
          </p>
        </Message>
        <Form>
          <Form.Field>
            <label>Upload Completed Template</label>
            <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} />
          </Form.Field>
        </Form>
        {results && (
          <div style={{ marginTop: "1em", maxHeight: 300, overflowY: "auto" }}>
            <Header as="h4">Bulk Upload Results</Header>
            <div>{renderResults(results)}</div>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        {results ? (
          <Button color="blue" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button color="red" onClick={handleClose}>
              Cancel
            </Button>
            <Button primary onClick={handleUpload} disabled={!file}>
              Upload
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
}
